import { createTheme } from '@mui/material';
import boddVariables from '../../../variables.module.scss';

// noinspection ES6UnusedImports
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-data-grid/themeAugmentation';

declare module '@mui/material/styles' {
    interface Palette {
        white: Palette['primary'];
        grey10: Palette['primary'];
        grey25: Palette['primary'];
        grey50: Palette['primary'];
        grey100: Palette['primary'];
        grey200: Palette['primary'];
        grey300: Palette['primary'];
        grey400: Palette['primary'];
        grey500: Palette['primary'];
        grey600: Palette['primary'];
        boddBlack: Palette['primary'];
        errorRed: Palette['primary'];
        successGreen: Palette['primary'];
        warningOrange: Palette['primary'];
        brandGreen: Palette['primary'];
        brandBlue: Palette['primary'];
        brandYellow: Palette['primary'];
        brandRed: Palette['primary'];
        brandPink: Palette['primary'];
    }

    interface PaletteOptions {
        white: PaletteOptions['primary'];
        grey10: PaletteOptions['primary'];
        grey25: PaletteOptions['primary'];
        grey50: PaletteOptions['primary'];
        grey100: PaletteOptions['primary'];
        grey200: PaletteOptions['primary'];
        grey300: PaletteOptions['primary'];
        grey400: PaletteOptions['primary'];
        grey500: PaletteOptions['primary'];
        grey600: PaletteOptions['primary'];
        boddBlack: PaletteOptions['primary'];
        errorRed: PaletteOptions['primary'];
        successGreen: PaletteOptions['primary'];
        warningOrange: PaletteOptions['primary'];
        brandGreen: PaletteOptions['primary'];
        brandBlue: PaletteOptions['primary'];
        brandYellow: PaletteOptions['primary'];
        brandRed: PaletteOptions['primary'];
        brandPink: PaletteOptions['primary'];
    }

    interface TypographyVariants {
        formHeading: React.CSSProperties;
    }

    // allow configuration using `createTheme`
    interface TypographyVariantsOptions {
        formHeading?: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    formHeading: true;
  }
}

export const defaultTheme = createTheme({});

// eslint-disable-next-line import/prefer-default-export
export const theme = createTheme({
    palette: {
        primary: {
            main: boddVariables.brandBlue,
        },
        secondary: {
            main: boddVariables.brandRed,
        },
        error: {
            main: boddVariables.errorRed,
        },
        warning: {
            main: boddVariables.warningOrange,
        },
        info: { main: boddVariables.brandBlue },
        success: { main: boddVariables.successGreen },
        text:
            {
                primary: boddVariables.boddBlack,
                secondary: boddVariables.grey500,
            },
        background: { default: boddVariables.grey10 },
        white: {
            main: boddVariables.white,
        },
        grey10: {
            main: boddVariables.grey10,
        },
        grey25: {
            main: boddVariables.grey25,
        },
        grey50: {
            main: boddVariables.grey50,
        },
        grey100: {
            main: boddVariables.grey100,
        },
        grey200: {
            main: boddVariables.grey200,
        },
        grey300: {
            main: boddVariables.grey300,
        },
        grey400: {
            main: boddVariables.grey400,
        },
        grey500: {
            main: boddVariables.grey500,
        },
        grey600: {
            main: boddVariables.grey600,
        },
        boddBlack: {
            main: boddVariables.boddBlack,
        },
        errorRed: {
            main: boddVariables.errorRed,
        },
        successGreen: {
            main: boddVariables.successGreen,
        },
        warningOrange: {
            main: boddVariables.warningOrange,
        },
        brandGreen: {
            main: boddVariables.brandGreen,
        },
        brandBlue: {
            main: boddVariables.brandBlue,
        },
        brandYellow: {
            main: boddVariables.brandYellow,
        },
        brandRed: {
            main: boddVariables.brandRed,
        },
        brandPink: {
            main: boddVariables.brandPink,
        },
    },
    typography: {
        h1: {
            fontFamily: 'Poppins',
            fontWeight: '700',
            fontSize: '3.125rem',
            lineHeight: '123.5%',
            letterSpacing: '0.25px',
        },
        h2: {
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '1.688rem',
            lineHeight: '116.7%',
        },
        h3: {
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '1.25rem',
            lineHeight: '116.7%',
        },
        h4: {
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '1rem',
            lineHeight: '150%',
            letterSpacing: '0.15px',
        },
        h5: {
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '0.875rem',
            lineHeight: '150%',
            letterSpacing: '0.15px',
        },
        formHeading: {
            fontFamily: 'Poppins',
            fontWeight: '600',
            fontSize: '1rem',
            lineHeight: '150%',
            letterSpacing: '0.15px',
        },
    },
    components: {
        MuiAlert: {
          styleOverrides: {
              standardError: {
                  color: boddVariables.boddBlack,
              },
          },
        },
        MuiDatePicker: {
            defaultProps: {
                format: 'DD-MM-YYYY',
            },
        },
        MuiTextField: {
            defaultProps: {
                variant: 'outlined',
                size: 'small',
            },
            styleOverrides: {
                root: {},
            },

        },
        MuiFormControl: {
            defaultProps: {
                size: 'small',
            },
        },
        MuiGrid: {
        },
        MuiDataGrid: {
            styleOverrides: {
                root: {
                      '.MuiDataGrid-virtualScrollerContent--overflowed .MuiDataGrid-row--lastVisible div.MuiDataGrid-cell': {
                        borderBottomColor: `${boddVariables.grey50}`,
                      },
                },
            },
        },
        MuiSvgIcon: {
          styleOverrides: {
              root: {
              },
          },
        },
        MuiIconButton: {
          styleOverrides: {
              root: {
                  color: boddVariables.grey500,
              },
          },
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:hover': {
                        background: boddVariables.brandLightBlue,
                        },
                },
            },
        },
        MuiTableCell: {
                    styleOverrides: {
                        root: {
                            whiteSpace: 'nowrap',
                            maxWidth: '300px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        },
                    },
                },
        MuiFormGroup: {
            styleOverrides: {
                root: {
                    '&:not(:last-child)': {
                        marginBottom: '1rem',
                    },
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '&.Mui-disabled': {
                        backgroundColor: boddVariables.grey25,
                    },
                },
            },
        },
        MuiDialog: {
          styleOverrides: {
              // clear out paper overrides
              paper: {
                  padding: 0,
                  [defaultTheme.breakpoints.down('md')]: {
                    padding: 0,
                  },
              },
          },
        },
        MuiDrawer: {
            styleOverrides: {
                // clear out paper overrides
                paper: {
                    padding: 0,
                    [defaultTheme.breakpoints.down('md')]: {
                                          padding: 0,
                    },
                },
            },
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontWeight: defaultTheme.typography.fontWeightMedium,
                    padding: '8px 24px',
                },
            },
        },
        MuiDialogContent: {
            styleOverrides: {
                root: {
                    padding: '8px 24px',
                },
            },
        },
        MuiDialogActions: {
            styleOverrides: {
                root: {
                    padding: '8px 24px',
                },
            },
        },
        MuiPagination: {
            defaultProps: {
                variant: 'outlined',
                shape: 'rounded',
            },
        },
        MuiTableHead: {
            styleOverrides: {
                root: {
                    padding: '8px 24px',
                    '> tr > th': {
                        fontWeight: defaultTheme.typography.fontWeightBold,
                    },
                },
            },
        },
        MuiTypography: {
            defaultProps: {
                variantMapping: {
                  formHeading: 'h4',
                },
            },
        },
        MuiContainer: {
            styleOverrides: {
                root: {
                    padding: defaultTheme.spacing(3),
                    [defaultTheme.breakpoints.down('md')]: {
                      padding: defaultTheme.spacing(2),
                    },
                },
            },
        },
        MuiMenu: {
          styleOverrides: {
              paper: {
                  padding: 0,
                  // clear out paper overrides
                  paper: {
                      padding: 0,
                      [defaultTheme.breakpoints.down('md')]: {
                                            padding: 0,
                      },
                  },
              },
          },
        },
        MuiPaper: {
            defaultProps: {
                elevation: 0,
            },
            styleOverrides: {
                root: {
                    border: '1px solid',
                    borderColor: boddVariables.grey50,
                    borderRadius: 0,
                    padding: defaultTheme.spacing(3),
                    [defaultTheme.breakpoints.down('md')]: {
                        padding: defaultTheme.spacing(2),
                    },
                },
            },
        },
        MuiButton: {
            defaultProps: {
                variant: 'contained',
            },
            styleOverrides: {
                contained: {
                    color: boddVariables.white,
                },
                text: {
                    '&.MuiButton-colorPrimary': {
                        color: boddVariables.grey600,
                    },
                    '&:hover': {
                        backgroundColor: boddVariables.grey25,
                    },
                },
                root: {
                    textTransform: 'none',
                    '&.Mui-disabled': {
                        backgroundColor: boddVariables.grey25,
                    },
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
        },
        MuiTabs: {
            defaultProps: {
                sx: {
                   borderBottom: 1,
                   borderColor: 'divider',
                   display: 'flex',
                   flexDirection: 'column',
                   width: '100%',
                   maxWidth: 'calc(100vw - 32px)',
                   mb: 1,
                },
            },
        },
        MuiLink: {
            defaultProps: {
                underline: 'always',
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    backgroundColor: boddVariables.grey25,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    zIndex: 1300,
                    height: 39,
                    border: 0,
                    padding: 0,
                    [defaultTheme.breakpoints.down('md')]: {
                        padding: 0,
                    },
                },
            },
        },
        MuiAutocomplete: {
            styleOverrides: {
                paper: {
                    padding: 0,
                    // clear out paper overrides
                    paper: {
                        padding: 0,
                        [defaultTheme.breakpoints.down('md')]: {
                                              padding: 0,
                        },
                    },
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    marginBottom: 0,
                },
            },
        },
        MuiBreadcrumbs: {
            styleOverrides: {
                root: {
                    marginBottom: '4px',
                },
            },
        },
        MuiToggleButtonGroup: {
            styleOverrides: {
                root: {
                    backgroundColor: boddVariables.grey50,
                    borderRadius: '4px',
                    border: 0,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: {
                    padding: 0,
                    margin: 4,
                    borderRadius: '4px',
                    '&.Mui-selected, &.Mui-selected:hover': {
                        backgroundColor: boddVariables.brandBlue,
                    },
                    border: 0,
                    '&.MuiToggleButtonGroup-grouped': {
                        borderRadius: '4px !important',
                    },
                },
            },
        },
        MuiTooltip: {
            defaultProps: {
                enterTouchDelay: 0,
            },
        },
    },
});
